// Normative reference ranges + evaluator.
//
// Improvement over OPED's flat ranges: where a metric is genuinely height- or
// speed-dependent (step length), the range is parameterized. Ranges are seed
// defaults — versioned so a calibration study can replace them cleanly.

export const NORMS_VERSION = "phase2a-seed-1";

export interface NormRange {
  low: number;
  high: number;
  unit: string;
  /** If true, only exceeding `high` is abnormal (e.g. variability). */
  upperOnly?: boolean;
}

export interface NormContext {
  heightCm?: number;
}

export type NormPosition = "below" | "in" | "above";
export type NormSeverity = "NORMAL" | "MILD" | "MODERATE" | "SEVERE";

export interface NormEval {
  range: NormRange;
  position: NormPosition;
  severity: NormSeverity;
}

/** Reference ranges keyed by clinical-metric id. */
export function referenceRange(key: string, ctx: NormContext = {}): NormRange | null {
  const h = ctx.heightCm;
  switch (key) {
    case "cadenceStepsPerMin":
      return { low: 90, high: 115, unit: "steps/min" };
    case "speedKmh":
      return { low: 2.0, high: 5.0, unit: "km/h" };
    case "doubleSupportPct":
      return { low: 15, high: 35, unit: "%" };
    case "stanceTimePct":
      return { low: 55, high: 65, unit: "%" };
    case "stepTimeS":
      return { low: 0.45, high: 0.62, unit: "s" };
    case "gaitVariabilityPct":
      return { low: 0, high: 8, unit: "%", upperOnly: true };
    case "symmetryIndexPct":
      return { low: 0, high: 8, unit: "SI", upperOnly: true };
    case "stepLengthCm":
      // Height-adjusted: step length ≈ 0.38–0.50 × stature.
      return h
        ? { low: 0.38 * h, high: 0.5 * h, unit: "cm" }
        : { low: 55, high: 80, unit: "cm" };
    default:
      return null;
  }
}

/** Evaluate a value against a range → position + severity band. */
export function evaluateNorm(value: number, range: NormRange): NormEval {
  const width = Math.max(1e-6, range.high - range.low);
  let position: NormPosition = "in";
  let dist = 0; // distance outside the range, in band-widths

  if (value < range.low && !range.upperOnly) {
    position = "below";
    dist = (range.low - value) / width;
  } else if (value > range.high) {
    position = "above";
    dist = (value - range.high) / width;
  }

  let severity: NormSeverity = "NORMAL";
  if (position !== "in") {
    severity = dist <= 0.5 ? "MILD" : dist <= 1.5 ? "MODERATE" : "SEVERE";
  }
  return { range, position, severity };
}
